import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { markMfaStepUpFailed } from '../adminMfaStepUp';
import { decideOidcApiFailure } from './oidcCore';
import {
  getOidcAccessToken,
  hasOidcRefreshToken,
  lastOidcMfaStepUpAt,
  lastOidcPhrStepUpAt,
  lastOidcReauthorizeAt,
  reauthorizeAfterUnauthorized,
  reauthorizeForMfa,
  reauthorizeForPhr,
  refreshOidcTokens,
} from './oidcSession';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** OIDC mode: set on the single retry after a token refresh so a 401 cannot loop. */
    oidcRefreshAttempted?: boolean;
    /** OIDC mode: the access token this request was sent with. */
    oidcSentWithToken?: string | null;
  }
}

/** Request interceptor body: attach the in-memory OIDC access token. */
export function attachOidcBearer(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const t = getOidcAccessToken();
  config.oidcSentWithToken = t;
  if (t) config.headers.Authorization = `Bearer ${t}`;
  else delete config.headers.Authorization;
  return config;
}

/**
 * Response-error handler for admin-api and the verification dashboards in OIDC mode:
 * 401 → one refresh + retry, then a (loop-guarded) top-level re-authorize;
 * 403 MFA_REQUIRED → re-authorize with `max_age=0&prompt=login&acr_values=mfa` back to this route
 * (or the MFA banner when a step-up just happened and the API still refuses);
 * 403 PHR_REQUIRED → re-authorize with `acr_values=phr` (a passkey sign-in), loop-guarded the same way.
 */
export async function handleOidcAxiosError(client: AxiosInstance, err: unknown): Promise<unknown> {
  const ax = err as AxiosError<{ code?: unknown }>;
  const config = ax.config as InternalAxiosRequestConfig | undefined;
  const status = ax.response?.status;
  if (!config || status === undefined) return Promise.reject(err);

  const code = ax.response?.data && typeof ax.response.data === 'object' ? ax.response.data.code : undefined;
  const current = getOidcAccessToken();

  // Another request already refreshed: just re-send with the new token.
  if (
    status === 401 &&
    !config.oidcRefreshAttempted &&
    current &&
    config.oidcSentWithToken !== undefined &&
    config.oidcSentWithToken !== current
  ) {
    return client.request({ ...config, oidcRefreshAttempted: true });
  }

  const decide = (refreshAttempted: boolean) =>
    decideOidcApiFailure({
      status,
      code,
      refreshAttempted,
      hasRefreshToken: hasOidcRefreshToken(),
      nowMs: Date.now(),
      lastReauthorizeAtMs: lastOidcReauthorizeAt(),
      lastMfaStepUpAtMs: lastOidcMfaStepUpAt(),
      lastPhrStepUpAtMs: lastOidcPhrStepUpAt(),
    });

  let action = decide(config.oidcRefreshAttempted === true);
  if (action === 'refresh') {
    if (await refreshOidcTokens()) return client.request({ ...config, oidcRefreshAttempted: true });
    action = decide(true);
  }
  if (action === 'reauthorize') reauthorizeAfterUnauthorized();
  else if (action === 'mfa-step-up') reauthorizeForMfa();
  else if (action === 'phr-step-up') reauthorizeForPhr();
  else if (action === 'banner') markMfaStepUpFailed(getOidcAccessToken());
  return Promise.reject(err);
}
